import React, { useState, useEffect, useRef } from 'react';
import imageCompression from 'browser-image-compression';

const AddMedia = ({ onSave, onCancel }) => {
  const [link, setLink] = useState('');
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState({ video: false, photo: false });

  const MAX_VIDEOS = 5;
  const MAX_PHOTOS = 5;
  const MAX_VIDEO_DURATION_SECONDS = 60.6;
  const MAX_PHOTO_SIZE_MB = 3;
  const COMPRESSION_MAX_WIDTH = 400;

  // --- START: CORRECTED URL CLEANUP LOGIC ---
  const allFilesRef = useRef([]);

  useEffect(() => {
    // This keeps the ref updated with the latest combined file list
    allFilesRef.current = [...videos, ...photos];
  }, [videos, photos]);

  useEffect(() => {
    // This effect's cleanup runs ONLY when the component unmounts
    return () => {
      // Access the ref to get the final list of files and revoke their URLs
      allFilesRef.current.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []); // Empty dependency array is crucial for unmount-only cleanup
  // --- END: CORRECTED URL CLEANUP LOGIC ---

  const getVideoDuration = (file) =>
    new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = function () {
        reject(`Invalid video: ${file.name}`);
      };
      video.src = URL.createObjectURL(file);
    });

  const handleFileChange = async (e, type) => {
    setError('');
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setIsProcessing(prev => ({ ...prev, [type]: true }));

    const currentFiles = type === 'video' ? videos : photos;
    const maxFiles = type === 'video' ? MAX_VIDEOS : MAX_PHOTOS;
    const acceptedFileType = type === 'video' ? 'video/*' : 'image/*';
    const newValidFiles = [];
    const errorMessages = [];

    for (const file of files) {
      if (currentFiles.length + newValidFiles.length >= maxFiles) {
        errorMessages.push(`Limit of ${maxFiles} reached.`);
        break;
      }
      if (!file.type.startsWith(acceptedFileType.split('/')[0])) {
        errorMessages.push(`"${file.name}" has an invalid type.`);
        continue;
      }
      if (type === 'video') {
        try {
          const duration = await getVideoDuration(file);
          if (duration > MAX_VIDEO_DURATION_SECONDS) {
            errorMessages.push(`"${file.name}" is too long (Max: 1 min).`);
            continue;
          }
        } catch (err) {
          errorMessages.push(`Could not read "${file.name}".`);
          continue;
        }
      } else {
        if (file.size / 1024 / 1024 > MAX_PHOTO_SIZE_MB) {
          errorMessages.push(`"${file.name}" is too large (Max: ${MAX_PHOTO_SIZE_MB}MB).`);
          continue;
        }
      }
      newValidFiles.push(file);
    }

    if (errorMessages.length > 0) {
      setError(errorMessages.join(' '));
    }
    
    if (newValidFiles.length > 0) {
      const filesWithPreview = newValidFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      }));
      if (type === 'video') {
        setVideos(prev => [...prev, ...filesWithPreview]);
      } else {
        setPhotos(prev => [...prev, ...filesWithPreview]);
      }
    }
    setIsProcessing(prev => ({ ...prev, [type]: false }));
  };

  const handleRemove = (index, type) => {
    const fileList = type === 'video' ? videos : photos;
    const fileToRemove = fileList[index];

    // This part is correct: clean up the URL of the specific file being removed.
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    
    if (type === 'video') {
      setVideos(prev => prev.filter((_, i) => i !== index));
    } else {
      setPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const compressedPhotos = await Promise.all(
        photos.map(async (photoFile, index) => {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: COMPRESSION_MAX_WIDTH,
            useWebWorker: true,
          };
          const compressedBlob = await imageCompression(photoFile, options);
          const extension = photoFile.name.split('.').pop() || 'jpg';
          return new File([compressedBlob], `photo${index + 1}.${extension}`, { type: compressedBlob.type });
        })
      );
      const renamedVideos = videos.map((videoFile, index) => {
        const extension = videoFile.name.split('.').pop() || 'mp4';
        return new File([videoFile], `video${index + 1}.${extension}`, { type: videoFile.type });
      });
      onSave({ link, media: { videos: renamedVideos, photos: compressedPhotos } });
    } catch (err) {
      setError("Could not process media. Please try again.");
      console.error("Error during save process:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // MediaGrid sub-component remains the same as the last version
  const MediaGrid = ({ type, files, isProcessing }) => {
    const maxFiles = type === 'video' ? MAX_VIDEOS : MAX_PHOTOS;

    return (
      <div className="media-grid">
        {files.map((file, index) => (
          <div key={index} className="grid-item">
            {type === 'video' ? (
              <video src={file.preview} className="media-preview" muted playsInline />
            ) : (
              <img src={file.preview} alt={`preview ${index}`} className="media-preview" />
            )}
            <button
              onClick={() => handleRemove(index, type)}
              className="remove-button"
              aria-label="Remove media"
            >
              &times;
            </button>
          </div>
        ))}
        {files.length < maxFiles && (
          <label className={`add-media-label ${isProcessing ? 'loading' : ''}`}>
             {isProcessing ? (
               <div className="loading-text">Processing...</div>
             ) : (
              <>
                <span className="add-media-icon">+</span>
                <span className="add-media-text">Add</span>
              </>
            )}
            <input
              type="file"
              className="hidden-input"
              multiple
              accept={type === 'video' ? 'video/*' : 'image/*'}
              onChange={(e) => handleFileChange(e, type)}
              disabled={isProcessing}
            />
          </label>
        )}
      </div>
    );
  };

  // The main return/render block also remains the same
  return (
    <div className="add-media-overlay">
      <div className="add-media-modal">
        {isLoading && (
            <div className="loading-overlay">
                <div className="loading-text">Processing...</div>
            </div>
        )}
        <div className="modal-header">
          <h2>Add Media</h2>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          <div>
            <label htmlFor="link-input" className="form-label">Link (Optional)</label>
            <input
              type="text"
              id="link-input"
              placeholder="https://example.com"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="text-input"
            />
          </div>
          <div>
            <h3 className="section-title">Videos</h3>
            <p className="helper-text">Max {MAX_VIDEOS} files, 1 minute each.</p>
            <MediaGrid type="video" files={videos} isProcessing={isProcessing.video} />
          </div>
          <div>
            <h3 className="section-title">Photos</h3>
            <p className="helper-text">Max {MAX_PHOTOS} files, {MAX_PHOTO_SIZE_MB}MB each.</p>
            <MediaGrid type="photo" files={photos} isProcessing={isProcessing.photo} />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onCancel} className="btn btn-cancel" disabled={isLoading}>Cancel</button>
          <button onClick={handleSave} className="btn btn-save" disabled={isLoading}>Save</button>
        </div>
      </div>
    </div>
  );
};
export default AddMedia;